// Плитки «Избранного» по компонентам Figma (HQ-1886: Fav Club Item I1073:20355;13929:28462 · Fav League Item
// I1073:20766;13929:28662 · Fav Player Item I1073:19966;13929:28052): тёмная карточка 16, слой flag_icon фоном, сетка 2 колонки. Нажатие — страница сущности; звезды на плитке нет, как в макете
// (HQ-1273): из избранного убирают звездой на странице сущности, плитка уходит сразу по нашему набору.
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import type { LeagueCard, PlayerCard } from '../../api/favoritesTypes';
import type { TeamCard } from '../../api/homeTypes';
import { t } from '../../i18n/i18n';
import { paths } from '../../nav';
import type { FavType } from '../../session/favorites';
import { Avatar } from '../../ui/Avatar';
import { TeamLogo } from '../../ui/TeamLogo';
import { leagueRecord, leagueTop, nextMatchLines, playerKeyNumber, playerSubtitle, rankFlag, rankLabel, rankLine, splitName } from './favModel';
import styles from './FavTiles.module.css';

export function TileGrid({ children }: { children: ReactNode }) {
  return <div className={styles.grid}>{children}</div>;
}

type Variant = 'team' | 'league' | 'player';
const OPEN: Record<Variant, string> = { team: styles.openTeam!, league: styles.openLeague!, player: styles.openPlayer! };

/** Фон — слой flag_icon макета: круг с подложкой rgba(0,0,0,.2), картинка contain, 0.35, blur, поворот 25.67°.
 *  Нет картинки — слоя нет, плитка ровного цвета. */
function Tile({ type, variant, id, name, image, onOpen, children }: {
  type: FavType; variant: Variant; id: number; name: string; image: string | null; onOpen: () => void; children: ReactNode;
}) {
  return (
    <div className={styles.tile} data-fav-tile={`${type}:${id}`}>
      {image && <img className={`${styles.backdrop} ${variant === 'player' ? styles.backdropPlayer : ''}`} src={image} alt=""
        aria-hidden="true" loading="lazy" decoding="async" />}
      <button className={`${styles.open} ${OPEN[variant]}`} aria-label={name} onClick={onOpen}>{children}</button>
    </div>
  );
}

export function TeamTile({ card }: { card: TeamCard }) {
  const navigate = useNavigate();
  const next = card.next_match ? nextMatchLines(card.next_match, card.id) : null;
  const rank = rankLabel(card);
  const flag = rankFlag(card);
  const line = next ? null : rankLine(card);
  return (
    <Tile type="team" variant="team" id={card.id} name={card.name} image={card.image} onOpen={() => navigate(paths.team(card.id))}>
      <span className={styles.head}>
        <span className={styles.logo}><TeamLogo src={card.image} code={card.code} size={32} /></span>
        <span className={styles.teamName}>{card.name}</span>
      </span>
      {(rank || next || line) && (
        <span className={styles.foot}>
          {rank && (
            <span className={styles.indicators}>
              {flag && <span className={styles.logo}><TeamLogo src={flag} code={card.rank?.league?.code ?? null} size={16} /></span>}
              {rank}
            </span>
          )}
          {next && <span className={styles.lines}><span className={styles.lineStrong}>{next.title}</span><span>{next.when}</span></span>}
          {line && <span className={styles.lines}><span className={styles.dim}>{line}</span><span>{t('tg_fav_next_tba')}</span></span>}
        </span>
      )}
    </Tile>
  );
}

export function LeagueTile({ card }: { card: LeagueCard }) {
  const navigate = useNavigate();
  const top = leagueTop(card);
  return (
    <Tile type="league" variant="league" id={card.id} name={card.name} image={card.image} onOpen={() => navigate(paths.league(card.id))}>
      <span className={styles.head}>
        <span className={`${styles.logoRound} ${styles.logo}`}><TeamLogo src={card.image} code={card.code} size={36} /></span>
        <span className={styles.names}>
          <span className={styles.leagueName}>{card.name}</span>
          {card.country && <span className={styles.leagueCountry}>{card.country}</span>}
        </span>
      </span>
      {top.length > 0 && (
        <span className={styles.table}>
          {top.map((row, index) => (
            // ключ — позиция: до жеребьёвки у команд бывает один id «TBC»
            <span key={index} className={styles.tableRow}>
              <span className={styles.logo}><TeamLogo src={row.team.image} code={row.team.code} size={16} /></span>
              <span className={styles.tableTeam}>{row.team.code || row.team.name}</span>
              <TablePoints row={row} />
            </span>
          ))}
        </span>
      )}
    </Tile>
  );
}

/** «8-2, 16» и «pts» строки топ-3 (макет: два текста с зазором 2); запись W-L неразрывна, нет won/lost — только очки. */
function TablePoints({ row }: { row: NonNullable<LeagueCard['standings_top']>[number] }) {
  const record = leagueRecord(row);
  return (
    <span className={styles.tablePts}>
      <span className={styles.record}>{record ? `${record} ${row.points}` : String(row.points)}</span>
      <span className={styles.dim}>{t('tg_fav_pts_unit')}</span>
    </span>
  );
}

/** Игрок по компоненту: фото, имя/фамилия, «Wicketkeeper • 35 y.o.», внизу значок команды и плашка числа. Клуба
 *  словами и ближайшего матча в компоненте нет. Значок и фон — card.team.image; нет картинки — нет ни того, ни другого. */
export function PlayerTile({ card }: { card: PlayerCard }) {
  const navigate = useNavigate();
  const { first, last } = splitName(card.name);
  const subtitle = playerSubtitle(card);
  const key = playerKeyNumber(card);
  const teamImage = card.team?.image ?? null;
  return (
    <Tile type="player" variant="player" id={card.id} name={card.name} image={teamImage} onOpen={() => navigate(paths.player(card.id))}>
      <span className={styles.head}>
        <span className={styles.photo}><Avatar src={card.image} name={card.name} size={36} kind="person" /></span>
        <span className={styles.names}>
          {first && <span className={styles.firstName}>{first}</span>}
          <span className={styles.lastName}>{last}</span>
        </span>
      </span>
      {subtitle && <span className={`${styles.subtitle} ${styles.wrap}`}>{subtitle}</span>}
      {(teamImage || key) && (
        <span className={styles.playerFoot}>
          {teamImage ? <span className={styles.logo}><TeamLogo src={teamImage} code={card.team?.code} size={16} /></span> : <span />}
          {key && <span className={styles.keyNumber}>{key}</span>}
        </span>
      )}
    </Tile>
  );
}
